import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["data-state"]
const _hoisted_2 = { class: "flex items-start justify-between gap-6" }
const _hoisted_3 = { class: "min-w-0" }
const _hoisted_4 = { class: "text-[11px] uppercase tracking-[0.08em] text-gray-400" }
const _hoisted_5 = { class: "mt-2 text-sm text-gray-300" }
const _hoisted_6 = {
  key: 0,
  class: "text-right shrink-0"
}
const _hoisted_7 = { class: "text-[11px] uppercase tracking-[0.08em] text-gray-400" }
const _hoisted_8 = { class: "text-sm font-bold mt-1" }

import { computed } from 'vue'

import NeueButton from '@/components/neue/NeueButton.vue'

import { BUDGET_CARDS, deltaOf, headerState, money, monthLabel, nextUpSentence, PACE_CARDS } from './marketingSeries'


export default {
  __name: 'MarketingMonthHeader',
  props: {
  agent: { type: Object, default: null },
  plan: { type: Object, default: null },
  previousSummary: { type: Object, default: null },
  handle: { type: String, default: '' },
  building: { type: Boolean, default: false },
  paceLabel: { type: String, default: '' }
},
  emits: ['resume', 'turn-on'],
  setup(__props, { emit: __emit }) {

const props = __props
const emit = __emit

const state = computed(() => headerState({ agent: props.agent, plan: props.plan, building: props.building }))
const summary = computed(() => props.plan?.attributes?.summary || {})
const items = computed(() => props.plan?.items || [])
const periodMonth = computed(() => (props.plan?.attributes?.period_start || new Date().toISOString()).slice(0, 7))
const month = computed(() => monthLabel(periodMonth.value).replace(/ \d{4}$/, ''))
const delta = computed(() =>
  props.previousSummary ? deltaOf(summary.value.revenue_cents || 0, props.previousSummary.revenue_cents) : null
)
const playsRan = computed(
  () =>
    items.value.filter(i => ['scheduled', 'sending', 'sent', 'done', 'approved'].includes(i.attributes?.status)).length
)
const LANE_NAMES = { online: 'one online', offline: 'one offline', retention: 'one for regulars' }
const lanes = computed(() => {
  const present = [...new Set(items.value.map(i => i.attributes?.lane))].map(l => LANE_NAMES[l]).filter(Boolean)
  return present.length ? present.join(', ').replace(/^./, c => c.toUpperCase()) + '.' : ''
})
const pace = computed(() => {
  if (props.paceLabel) return props.paceLabel
  const paceTitle = PACE_CARDS.find(c => c.value === props.agent?.attributes?.pace)?.title || ''
  const budget = BUDGET_CARDS.find(c => c.value === props.agent?.attributes?.budget_tier)?.title || ''
  return [paceTitle, budget].filter(Boolean).join(' · ')
})

const eyebrow = computed(() =>
  state.value === 'none' ? `Marketing agent · ${props.handle}` : `${month.value} plan · ${props.handle}`
)

const headline = computed(() => {
  switch (state.value) {
    case 'none':
      return 'Two or three marketing plays a month, chosen for your business, written in your voice, sent only when you say so.'
    case 'building':
      return 'Building your first plan…'
    case 'ready':
      return `${items.value.length} ${items.value.length === 1 ? 'play' : 'plays'} ready for you`
    case 'paused':
      return 'Paused'
    case 'off':
      return 'Marketing is off'
    case 'closed':
      return `${money(summary.value.revenue_cents)} this month`
    default:
      return ''
  }
})

const subline = computed(() => {
  switch (state.value) {
    case 'none':
      return 'Built from what 300+ food business owners say actually works. Every text play gets its own code so you see the orders it drove.'
    case 'building':
      return 'Reading your orders, your calendar and your customer list. About a minute.'
    case 'ready':
      return `${lanes.value} Approve, edit or skip each.`.trim()
    case 'progress':
      return `${summary.value.orders || 0} orders from ${playsRan.value} ${playsRan.value === 1 ? 'play' : 'plays'} · ${summary.value.reached || 0} customers reached`
    case 'paused':
      return 'No new plays until you set a pace.'
    case 'off':
      return 'Nothing sends. Your codes stay live until they expire.'
    case 'closed':
      return "Next month's plan arrives on the 1st."
    default:
      return ''
  }
})

const rightSide = computed(() => {
  if (state.value === 'building') return { label: 'Pace', text: pace.value }
  if (state.value === 'ready') return { label: 'Next up', text: 'Nothing sends until you approve' }
  if (state.value === 'progress') {
    const next = nextUpSentence(props.plan)
    return next ? { label: 'Next up', text: next } : null
  }
  return null
})

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("section", {
    class: "bg-gray-900 text-white rounded-2xl p-6",
    "data-header": "",
    "data-state": state.value
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("p", _hoisted_4, _toDisplayString(eyebrow.value), 1),
        _createElementVNode("p", {
          class: _normalizeClass(["mt-1 text-2xl font-extrabold tracking-tight leading-tight", state.value === 'building' ? 'animate-pulse' : ''])
        }, [
          (state.value === 'progress')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(_toDisplayString(_unref(money)(summary.value.revenue_cents)) + " ", 1),
                (delta.value)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      "data-delta": "",
                      class: _normalizeClass(["text-base font-bold ml-1", delta.value.up ? 'text-emerald-200' : 'text-red-300'])
                    }, _toDisplayString(delta.value.up ? '↑' : '↓') + " " + _toDisplayString(delta.value.pct) + "% ", 3))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createTextVNode(_toDisplayString(headline.value), 1)
              ], 64))
        ], 2),
        _createElementVNode("p", _hoisted_5, _toDisplayString(subline.value), 1)
      ]),
      (rightSide.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("p", _hoisted_7, _toDisplayString(rightSide.value.label), 1),
            _createElementVNode("p", _hoisted_8, _toDisplayString(rightSide.value.text), 1)
          ]))
        : (state.value === 'paused')
          ? (_openBlock(), _createBlock(NeueButton, {
              key: 1,
              type: "secondary",
              "data-resume": "",
              onClick: _cache[0] || (_cache[0] = $event => (emit('resume')))
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode(" Resume ", -1)
              ]))]),
              _: 1
            }))
          : (state.value === 'off')
            ? (_openBlock(), _createBlock(NeueButton, {
                key: 2,
                type: "secondary",
                "data-turn-on": "",
                onClick: _cache[1] || (_cache[1] = $event => (emit('turn-on')))
              }, {
                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                  _createTextVNode(" Turn back on ", -1)
                ]))]),
                _: 1
              }))
            : _createCommentVNode("", true)
    ])
  ], 8, _hoisted_1))
}
}

}